import React from "react";

import type { SelectChangeEvent } from "@mui/material";
import Typography from "@mui/material/Typography";

import useUserSettings from "@app/analytics/hooks/settings/useUserSettings";
import type {
  UserEventGroup,
  UserEventGroupPatch,
} from "@app/analytics/types/eventGroupTypes";
import useAuth from "@app/common/auth/useAuth";
import useFeatureAccess from "@app/common/hooks/useFeatureAccess";
import type { ParticipantAccessLevel } from "@app/common/types";
import * as UserUtils from "@app/common/utils/userUtils";

import Dropdown, { DropdownMenuItem } from "@rui/Dropdown";
import { getSelectedFromOptions } from "@rui/Dropdown/utils";
import { Column, Row } from "@rui/Stack";
import Switch from "@rui/Switch";
import TooltipIcon from "@rui/TooltipIcon";
import TooltipWrapper from "@rui/TooltipWrapper";

import {
  ShareableRole,
  CONTENT_FOR_PREDICTED_ROLE,
  MEETING_RECAP_OPTIONS,
} from "./constants";
import {
  ContentContainer,
  RoleDescription,
  WarningMessage,
  ParticipantRoleToggle,
  Setting,
} from "./styles";
import * as UpcomingEventUtils from "../utils";
import { UpcomingEventMeetingRecapRecipient } from "../utils";

const TOOLTIPS = {
  meetingIsPrivate: "Cannot be changed for private reports",
  internalAccessGenericDomain: (domain: string) =>
    `The domain associated with your Read account ${
      domain ? `(@${domain})` : ""
    } is generic, so all participants are considered external. Use the external participant access setting below to control report access.`,
  enforcedByWorkspace: "Enforced by your Workspace",
};

// TODO: ENG-11035 enable this?
const ENABLE_MEETING_RECAP_SETTING = false;

const AccessAndDistributionTab = ({
  event,
  externalParticipantsRole,
  internalParticipantsRole,
  onChangeSetting,
  pendingUpdates,
}: {
  event: UserEventGroup;
  externalParticipantsRole: ParticipantAccessLevel;
  internalParticipantsRole: ParticipantAccessLevel;
  onChangeSetting: (update: UserEventGroupPatch) => void;
  pendingUpdates: UserEventGroupPatch;
}) => {
  const { user } = useAuth();
  const { userSettings } = useUserSettings();
  const {
    canEditInternalParticipantAccessLevel,
    canEditExternalParticipantAccessLevel,
    canEditSendMeetingRecap,
  } = useFeatureAccess();

  const meetingIsPrivate = UpcomingEventUtils.eventIsPrivate(
    event,
    pendingUpdates
  );
  const predictedRole = event.predictedAccessLevel!;

  const meetingRecapRecipients = UpcomingEventUtils.getMeetingRecapRecipients(
    event,
    pendingUpdates,
    userSettings
  );

  const { title, description, note } =
    CONTENT_FOR_PREDICTED_ROLE[predictedRole as ShareableRole];

  const userHasCompanyEmail = UserUtils.hasCompanyEmail(user);
  const userDomain = UserUtils.getEmailDomain(user);

  const handleToggleIsPrivate = (
    event: React.SyntheticEvent,
    checked: boolean
  ) => {
    onChangeSetting({ isPrivate: checked });
  };

  const handleChangeAccessLevel =
    (group: "internal" | "external") =>
    (event: React.MouseEvent, value: string) => {
      if (!value) return;
      const newAccessLevel = value as ParticipantAccessLevel;
      if (group === "internal") {
        onChangeSetting({ internalParticipantAccessLevel: newAccessLevel });
      } else {
        onChangeSetting({ externalParticipantAccessLevel: newAccessLevel });
      }
    };

  const handleChangeMeetingRecapRecipients = (
    event: SelectChangeEvent<UpcomingEventMeetingRecapRecipient>
  ) => {
    const value = event.target.value;
    onChangeSetting({
      sendMeetingRecapToMe: value === "everyone" || value === "just_me",
      sendMeetingRecapToParticipantsWithAccess: value === "everyone",
    });
  };

  return (
    <>
      <RoleDescription spacing={1}>
        <Row variant="left" spacing={0.5}>
          <Typography variant="labelMed">Your predicted role</Typography>
          <TooltipIcon
            placement="bottom"
            title="How are we determining role? Your role is determined based on a combination of your settings and the session owner's settings. Ownership is determined based on the video conference owner."
          />
        </Row>
        <Column spacing="2px">
          <Typography variant="bodyPrimaryMed">{title}</Typography>
          <Typography variant="labelReg" color="shades.neutral.600">
            {description}
          </Typography>
        </Column>
        <WarningMessage>Note: {note}</WarningMessage>
      </RoleDescription>
      <ContentContainer>
        {event.isVcOwner && (
          <Setting
            label="Make report private"
            description="Restricts participant access and prevents them from adding Read"
            spacing={1.5}
            wrap={false}
          >
            <Switch
              sx={{ m: 0 }}
              onChange={handleToggleIsPrivate}
              checked={meetingIsPrivate}
            />
          </Setting>
        )}
        <Setting
          label="Internal participants"
          description="Set meeting report access level"
        >
          <TooltipWrapper
            placement="top"
            show={!canEditInternalParticipantAccessLevel || meetingIsPrivate}
            title={
              meetingIsPrivate
                ? TOOLTIPS.meetingIsPrivate
                : userHasCompanyEmail
                  ? TOOLTIPS.enforcedByWorkspace
                  : TOOLTIPS.internalAccessGenericDomain(userDomain)
            }
          >
            <ParticipantRoleToggle
              onChange={handleChangeAccessLevel("internal")}
              selected={internalParticipantsRole}
              disabled={
                !canEditInternalParticipantAccessLevel || meetingIsPrivate
              }
            />
          </TooltipWrapper>
        </Setting>
        <Setting
          label="External participants"
          description="Set meeting report access level"
        >
          <TooltipWrapper
            placement="top"
            show={!canEditExternalParticipantAccessLevel || meetingIsPrivate}
            title={
              meetingIsPrivate
                ? TOOLTIPS.meetingIsPrivate
                : TOOLTIPS.enforcedByWorkspace
            }
          >
            <ParticipantRoleToggle
              onChange={handleChangeAccessLevel("external")}
              selected={externalParticipantsRole}
              disabled={
                !canEditExternalParticipantAccessLevel || meetingIsPrivate
              }
            />
          </TooltipWrapper>
        </Setting>
        {ENABLE_MEETING_RECAP_SETTING && (
          <Setting
            label="Meeting recap"
            description="Automatically send a recap to"
          >
            <TooltipWrapper
              placement="top"
              show={!canEditSendMeetingRecap}
              title={TOOLTIPS.enforcedByWorkspace}
            >
              <Dropdown
                name="meeting-recap-recipients"
                selectedItem={getSelectedFromOptions(
                  MEETING_RECAP_OPTIONS,
                  meetingRecapRecipients
                )}
                sx={{
                  "& fieldset": { borderColor: "shades.neutral.100" },
                  minWidth: "253px",
                }}
                disabled={!canEditSendMeetingRecap}
                onChange={handleChangeMeetingRecapRecipients}
              >
                {MEETING_RECAP_OPTIONS.map((option) => (
                  <DropdownMenuItem
                    key={option.value}
                    {...option}
                    selected={option.value === meetingRecapRecipients}
                  />
                ))}
              </Dropdown>
            </TooltipWrapper>
          </Setting>
        )}
      </ContentContainer>
    </>
  );
};

export default AccessAndDistributionTab;
